import React, { useEffect, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { gql } from '@apollo/client';
import { Button, FileListPreview, Icon, SectionHeading, StatusBadge, UploadField, useGraphQLMutation } from '@tviklax/ui-components';
import { SchedulingContextHeaderProps, ActivityLogEntry, AddNotePayload, ShippingPrepConfig, CarrierQuote, InitialBookingData } from './SchedulingContextTypes';
import { fetchSchedules, fetchCmaVessels, schedulesToVessels } from '../../../services/tviklaExpress';
import { SchedulingContextHeader } from './SchedulingContextHeader';
import { SchedulingTimeline } from './SchedulingTimeline';
import { SchedulingDetailPanel } from './SchedulingDetailPanel';
import { VesselSchedule } from './VesselSchedule';
import { SchedulingActions } from './SchedulingActions';
import { CarrierQuotations } from './CarrierQuotations';
import { AvailableVessels, Vessel } from './AvailableVessels';
import { SchedulingActivityLog } from './SchedulingActivityLog';
import { SchedulingAddNote } from './SchedulingAddNote.tsx';
import { SchedulingNotes, SavedNote } from './SchedulingNotes';
import { useAddNote } from './useAddNote';
import { useSLAHealth } from './useSLAHealth';
import { useShippingBill } from '../hooks/useShippingBill';
import { ShippingBillCard } from './ShippingBillCard';

export interface SchedulingContextProps extends SchedulingContextHeaderProps {
  backendOrderId?: number;
  orderShipmentId?: number;
  originPort?: string;
  destinationPort?: string;
  stages: {
    name: string;
    completedDate?: string;
  }[];
  stageDetails?: Record<string, any>;
  vessels?: {
    name: string;
    eta: string;
    cost: number;
    recommendation?: boolean;
    capacity?: string;
    containerCount?: number;
  }[];
  vesselsByCarrier?: Record<string, Vessel[]>;
  activityLog?: ActivityLogEntry[];
  notes?: SavedNote[];
  isLoadingStageDetails?: boolean;
  isLoadingVessels?: boolean;
  isLoadingActivities?: boolean;
  isLoadingNotes?: boolean;
  onUserAction?: (action: string) => void;
  showAddNote?: boolean;
  currentUser?: string;
  currentUserRole?: 'Ops' | 'Admin' | 'Viewer';
  shippingPrep?: ShippingPrepConfig;
  onUploadShippingDocument?: (documentName: string, file: File) => Promise<void>;
  onReplaceShippingDocument?: (documentName: string, file: File) => Promise<void>;
  onResolveDocumentUrl?: (filePath?: string) => string | null;
  initialBookingConfirmed?: InitialBookingData;
}

const sortNotesNewestFirst = (entries: SavedNote[]): SavedNote[] => {
  return [...entries].sort((left, right) => {
    const leftTs = new Date(left.createdAt || 0).getTime();
    const rightTs = new Date(right.createdAt || 0).getTime();
    return rightTs - leftTs;
  });
};

const mergeSavedNotes = (...noteGroups: Array<SavedNote[] | undefined>): SavedNote[] => {
  const merged = new Map<string, SavedNote>();

  noteGroups
    .flatMap((group) => group || [])
    .forEach((note) => {
      const fallbackKey = `${note.createdAt || ''}::${note.author || ''}::${note.content || ''}`;
      const key = String(note.id || fallbackKey);
      const existing = merged.get(key);

      if (!existing) {
        merged.set(key, note);
        return;
      }

      merged.set(key, {
        ...existing,
        ...note,
        id: existing.id || note.id || key,
        createdAt: existing.createdAt || note.createdAt,
      });
    });

  return sortNotesNewestFirst(Array.from(merged.values()));
};

const loadPersistedNotes = (storageKey: string, fallbackNotes: SavedNote[]): SavedNote[] => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return sortNotesNewestFirst(fallbackNotes);
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return sortNotesNewestFirst(fallbackNotes);
    }

    return mergeSavedNotes(parsed as SavedNote[], fallbackNotes);
  } catch {
    return sortNotesNewestFirst(fallbackNotes);
  }
};

const UPDATE_ORDER_SHIPMENT_MUTATION = gql`
  mutation UpdateOrderShipment($input: UpdateOrderShipmentInput!) {
    updateOrderShipment(input: $input) {
      id
      orderId
      shippingProvider
      shippingOptions
      shippingResponse
      updatedAt
    }
  }
`;

const CREATE_ORDER_SHIPMENT_MUTATION = gql`
  mutation CreateOrderShipment($data: CreateOrderShipmentsInput!) {
    createOrderShipments(data: $data) {
      id
      orderId
    }
  }
`;

const SchedulingContextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
`;

const ShippingSection = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: ${({ theme }) => theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  flex-wrap: wrap;
`;

const ReadinessBadgeGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  flex-wrap: wrap;
`;

const ReadinessStats = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.gray100};
  color: ${({ theme }) => theme.colors.gray700};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const ReadinessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
`;

const DocCard = styled.div<{ $status: 'pending' | 'uploaded' | 'verified' }>`
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-left: 4px solid
    ${({ $status, theme }) => ($status === 'verified' ? theme.colors.success : $status === 'uploaded' ? theme.colors.warning : theme.colors.error)};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  align-items: flex-start;
  min-height: 180px;
  background: ${({ theme }) => theme.colors.surface};
`;

const DocMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DocName = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray900};
`;

const DocStatus = styled.span<{ $status: 'pending' | 'uploaded' | 'verified' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${({ $status, theme }) => ($status === 'verified' ? theme.colors.successDark : $status === 'uploaded' ? theme.colors.warningDark : theme.colors.errorDark)};
`;

const HelperText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray600};
  line-height: 1.4;
`;

const DocActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: auto;
`;

// Quotes styled components removed in favor of CarrierQuotations component

const AfterSelectionCard = styled.div`
  border: 1px dashed ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.background};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const QuotesSection = styled.div<{ $disabled: boolean }>`
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;



const ShippingSummaryCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-left: 4px solid ${({ theme }) => theme.colors.success};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[5]};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
`;

const SummaryRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const SummaryRowLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray600};
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.02em;
`;

const SummaryRowValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray900};
`;

const RiskBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.errorLight};
  border: 1px solid ${({ theme }) => theme.colors.error};
  color: ${({ theme }) => theme.colors.errorDark};
  font-size: 13px;
  font-weight: 500;
`;

const ShippingWorkflowSteps = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[5]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.infoLight};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.info};
`;

const StepIndicator = styled.div<{ $active: boolean; $completed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-weight: 600;
  font-size: 13px;
  background: ${({ $active, $completed, theme }) =>
    $completed ? theme.colors.success : $active ? theme.colors.primary : theme.colors.gray200
  };
  color: ${({ $active, $completed, theme }) => ($active || $completed ? theme.colors.surface : theme.colors.gray500)};
`;

const StepLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray700};
  text-transform: uppercase;
  letter-spacing: 0.02em;
`;

const Divider = styled.div`
  width: 1px;
  height: 20px;
  background: ${({ theme }) => theme.colors.borderStrong};
`;


const NextStepsPanel = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: ${({ theme }) => theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const NextStepsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray800};
  text-transform: uppercase;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const NextStepsTable = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[2]};
  align-items: center;
`;

const NextStepsHeaderCell = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray600};
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const NextStepsCell = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gray800};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CompactCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[3]};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: ${({ theme }) => theme.spacing[2]};
  align-items: center;
`;

const CompactTitle = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray800};
`;

const CompactMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.gray700};
`;

const CompactCTA = styled(Button)`
  justify-self: flex-end;
`;

const ModalOverlay = styled.div<{ $dim?: number }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, ${({ $dim }) => $dim ?? 0.4});
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const PreviewModalPanel = styled.div`
  width: min(600px, 90vw);
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.colors.surface};
  z-index: 1;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.gray900};
`;

const PreviewModalBody = styled.div`
  flex: 1;
  overflow: auto;
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.background};
`;

const BreakupModalPanel = styled.div`
  width: min(600px, 90vw);
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  display: flex;
  flex-direction: column;
  max-height: 85vh;
  overflow: hidden;
`;

const BreakupBody = styled.div`
  padding: ${({ theme }) => theme.spacing[5]};
  overflow-y: auto;
`;

const BreakupSummaryCard = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing[5]};
`;

const BreakupSummaryLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray500};
  text-transform: uppercase;
  font-weight: 700;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const BreakupSummaryValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray900};
`;

const BreakupSummaryHint = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.gray500};
  margin-top: 4px;
`;

const BreakupSectionTitle = styled.h4`
  margin: 0 0 ${({ theme }) => theme.spacing[3]} 0;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray900};
  text-transform: uppercase;
  letter-spacing: 0.02em;
`;

const BreakupSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[5]};
`;

const BreakupRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const BreakupRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
`;

const BreakupRowLabel = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gray600};
`;

const BreakupRowValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray900};
`;

const BreakupTotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0 8px;
  border-top: 2px solid ${({ theme }) => theme.colors.gray900};
`;

const BreakupTotalLabel = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray900};
`;

const BreakupDetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const BreakupDetailLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray500};
  text-transform: uppercase;
  font-weight: 700;
`;

const BreakupDetailValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray900};
`;

const BreakupNote = styled.div`
  margin-top: ${({ theme }) => theme.spacing[5]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.warningLight};
  border-left: 3px solid ${({ theme }) => theme.colors.warning};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
`;

const BreakupNoteLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.warningDark};
  font-weight: 600;
`;

const BreakupNoteText = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.warningDark};
  margin-top: 4px;
`;

const ModalFooter = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.colors.gray200};
  display: flex;
  justify-content: flex-end;
  position: sticky;
  bottom: 0;
  background: ${({ theme }) => theme.colors.surface};
`;

export const SchedulingContextMain: React.FC<SchedulingContextProps> = ({
  orderId,
  backendOrderId,
  orderShipmentId,
  customerName,
  plant,
  currentStage,
  stageIndex,
  totalStages,
  healthStatus,
  promisedDate,
  lastUpdate,
  nextSystemAction,
  stages,
  stageDetails = {},
  vessels = [],
  vesselsByCarrier = {},
  activityLog = [],
  notes = [],
  isLoadingStageDetails = false,
  isLoadingVessels = false,
  isLoadingActivities = false,
  isLoadingNotes = false,
  onUserAction,
  showAddNote = false,
  currentUser,
  currentUserRole = 'Viewer',
  shippingPrep,
  originPort = 'INMAA',
  destinationPort = 'CNSHA',
  onUploadShippingDocument,
  onReplaceShippingDocument,
  onResolveDocumentUrl,
  initialBookingConfirmed,
}) => {
  const theme = useTheme();
  const notesStorageKey = `scheduling-notes-${orderId}`;
  const [activities, setActivities] = useState<ActivityLogEntry[]>(activityLog);
  const [savedNotes, setSavedNotes] = useState<SavedNote[]>(() => loadPersistedNotes(notesStorageKey, notes));
  const [health, setHealth] = useState(healthStatus);
  const [showAddNotePanel, setShowAddNotePanel] = useState(showAddNote);
  const bookingStorageKey = `scheduling-booking-${orderId}`;

  const buildInitialFromDb = (): { selectedQuote: CarrierQuote; selectedVessel: Vessel; bookingCreated: true } | null => {
    if (!initialBookingConfirmed) return null;
    const { carrierId, carrierName, vesselId, vesselName, cost, etaDays, freeDemurrageDays, vesselEta, vesselEtd } = initialBookingConfirmed;
    return {
      bookingCreated: true,
      selectedQuote: { id: carrierId, carrier: carrierName, etaDays, cost, transit: 'Sea', freeDemurrageDays },
      selectedVessel: { id: vesselId, name: vesselName, eta: vesselEta, nextDeparture: vesselEtd, capacity: 'N/A', containerCount: 0, availableContainers: 14 },
    };
  };

  const loadPersistedBooking = (): { selectedQuote: CarrierQuote | null; selectedVessel: Vessel | null; bookingCreated: boolean } => {
    try {
      const raw = localStorage.getItem(bookingStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        // If localStorage says confirmed but quote/vessel are missing, fall through to DB data
        if (parsed.bookingCreated && (!parsed.selectedQuote || !parsed.selectedVessel)) {
          const fromDb = buildInitialFromDb();
          if (fromDb) return fromDb;
        }
        console.debug(`[Scheduling] Loaded booking from localStorage for orderId=${orderId}:`, parsed);
        return parsed;
      }
    } catch (err) {
      console.error(`[Scheduling] Error loading booking from localStorage:`, err);
    }
    // No localStorage — seed from DB booking if available
    const fromDb = buildInitialFromDb();
    if (fromDb) {
      console.debug(`[Scheduling] Seeding booking state from DB for orderId=${orderId}`);
      return fromDb;
    }
    return { selectedQuote: null, selectedVessel: null, bookingCreated: false };
  };

  const persisted = loadPersistedBooking();
  const [selectedQuote, setSelectedQuote] = useState<CarrierQuote | null>(persisted.selectedQuote);
  const [selectedVessel, setSelectedVessel] = useState<Vessel | null>(persisted.selectedVessel);
  const [bookingCreated, setBookingCreated] = useState(persisted.bookingCreated);
  const [quoteSort, setQuoteSort] = useState<'price' | 'eta' | 'demurrage'>('price');
  const [breakupQuote, setBreakupQuote] = useState<CarrierQuote | null>(null);
  const [liveVesselsByCarrier, setLiveVesselsByCarrier] = useState<Record<string, Vessel[]>>({});
  const [loadingVessels, setLoadingVessels] = useState(false);

  // GraphQL mutation to save booking to backend
  const [updateOrderShipment] = useGraphQLMutation(UPDATE_ORDER_SHIPMENT_MUTATION, {
    onError: (err) => {
      console.error('[Scheduling] Failed to save booking to backend:', err);
    },
  });

  // Mutation to create a shipment record when one doesn't exist yet
  const [createOrderShipment] = useGraphQLMutation(CREATE_ORDER_SHIPMENT_MUTATION);

  // Track the shipment ID, including one created on-the-fly during booking
  const [resolvedShipmentId, setResolvedShipmentId] = React.useState<number | undefined>(orderShipmentId);

  const sb = useShippingBill({
    orderId,
    customerName,
    originPort,
    destinationPort,
  });

  // Debug: Log booking state changes
  useEffect(() => {
    console.debug(`[Scheduling] Booking state for orderId=${orderId}:`, {
      bookingCreated,
      selectedQuote: selectedQuote ? `${selectedQuote.carrier} ($${selectedQuote.cost})` : null,
      selectedVessel: selectedVessel ? selectedVessel.name : null,
    });
  }, [bookingCreated, selectedQuote, selectedVessel, orderId]);

  // CRITICAL: Reload booking state when orderId changes (order switch)
  useEffect(() => {
    console.debug(`[Scheduling] OrderId changed to ${orderId}, reloading booking state`);
    const restored = loadPersistedBooking();
    setSelectedQuote(restored.selectedQuote);
    setSelectedVessel(restored.selectedVessel);
    setBookingCreated(restored.bookingCreated);
  }, [orderId]);

  useEffect(() => {
    try {
      const toSave = { selectedQuote, selectedVessel, bookingCreated };
      console.debug(`[Scheduling] Saving booking to localStorage for orderId=${orderId}:`, toSave);
      localStorage.setItem(bookingStorageKey, JSON.stringify(toSave));
    } catch (err) {
      console.error(`[Scheduling] Error saving booking to localStorage:`, err);
    }
  }, [selectedQuote, selectedVessel, bookingCreated, orderId]);

  useEffect(() => {
    setSavedNotes((prev) => mergeSavedNotes(prev, notes));
  }, [notes]);

  useEffect(() => {
    try {
      localStorage.setItem(notesStorageKey, JSON.stringify(savedNotes));
    } catch {
      // ignore storage errors
    }
  }, [notesStorageKey, savedNotes]);

  // Fetch live vessels whenever the selected carrier changes
  useEffect(() => {
    if (!selectedQuote) return;
    const carrierId = selectedQuote.id; // e.g. 'cma-cgm', 'hapag-lloyd', 'maersk'
    setLoadingVessels(true);
    const today = new Date().toISOString().split('T')[0];

    const schedulePromise = fetchSchedules({
      origin_port: originPort,
      destination_port: destinationPort,
      departure_date: today,
      carrier: carrierId,
    });

    const cmaPromise = carrierId === 'cma-cgm'
      ? fetchCmaVessels()
      : Promise.resolve(undefined);

    Promise.all([schedulePromise, cmaPromise])
      .then(([schedules, cmaVessels]) => {
        const vessels = schedulesToVessels(schedules, carrierId, cmaVessels);
        setLiveVesselsByCarrier((prev) => ({ ...prev, [carrierId]: vessels as Vessel[] }));
      })
      .catch(() => {})
      .finally(() => setLoadingVessels(false));
  }, [selectedQuote?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const { getRecommendedHealth } = useSLAHealth(promisedDate, currentStage, health);

  const { handleSaveNote } = useAddNote({
    orderId,
    backendOrderId,
    orderShipmentId,
    onActivityLogUpdate: (newEntry) => {
      setActivities((prev) => [newEntry, ...prev]);
    },
    onHealthUpdate: setHealth,
  });

  const handleAddNoteSubmit = async (payload: AddNotePayload) => {
    // Auto-compute health based on SLA and note content
    const recommendedHealth = getRecommendedHealth(payload);
    if (recommendedHealth !== health) {
      setHealth(recommendedHealth);
    }

    const result = await handleSaveNote(payload);
    if (result.success) {
      const newNote: SavedNote = {
        id: `note-${Date.now()}`,
        createdAt: payload.timestamp || new Date().toISOString(),
        author: payload.author,
        noteType: payload.noteType,
        content: payload.content,
        impactLevel: payload.impactLevel,
        visibility: payload.visibility,
        riskCause: payload.riskCause,
        delay: payload.delay,
        delayCustom: payload.delayCustom,
        affectsStage: payload.affectsStage,
        nextAction: payload.nextAction,
        nextActionDueBy: payload.nextActionDueBy,
        notified: payload.notifications?.send
          ? {
              internal: payload.notifications.internal,
              customer: payload.notifications.customer,
              ops: payload.notifications.ops,
            }
          : undefined,
      };
      setSavedNotes((prev) => mergeSavedNotes([newNote], prev));
      setShowAddNotePanel(false);
    }
  };

  const handleUserAction = (action: string) => {
    if (action === 'add-note') {
      setShowAddNotePanel(true);
    }
    onUserAction?.(action);
  };

  const completionPct = (stageDetails?.completion as number | undefined) || 0;

  const shouldShowShippingPrep = useMemo(() => {
    if (!shippingPrep) return false;
    // overrideEnabled lets Ops and Admin view shipping prep regardless of stage
    if (shippingPrep.overrideEnabled && (currentUserRole === 'Ops' || currentUserRole === 'Admin')) return true;
    if (currentStage === 'Production Ready') return true;
    if (currentStage === 'Testing' && completionPct >= 80) return true;
    // Always show if a booking was already confirmed (persisted state)
    if (bookingCreated) return true;
    return false;
  }, [shippingPrep, currentStage, completionPct, currentUserRole, bookingCreated]);

  // Auto-generate SB draft the first time shipping prep becomes visible
  useEffect(() => {
    if (shouldShowShippingPrep && sb.state.status === 'not_started') {
      sb.generateDraft().catch(() => {});
    }
  }, [shouldShowShippingPrep]); // eslint-disable-line react-hooks/exhaustive-deps

  const { readinessState, readinessLabel, minDocsMet, readinessScore, docPct, bookingPct, customsPct } = useMemo(() => {
    if (!shippingPrep) {
      return { readinessState: 'blocked' as const, readinessLabel: 'Not Ready', readinessSymbol: '⛔', minDocsMet: false, readinessScore: 0, docPct: 0, bookingPct: 0, customsPct: 0 };
    }

    const preBookingDocs = shippingPrep.documents.filter((d) => d.required && !d.requiredAfterBooking);
    const preBookingCompleted = preBookingDocs.filter((d) => d.status === 'verified');
    const docPct = preBookingDocs.length > 0 ? (preBookingCompleted.length / preBookingDocs.length) * 100 : 0;

    const bookingPct = bookingCreated ? 100 : selectedQuote ? 50 : 0;
    const customsPct = 0;
    const readinessScore = (docPct * 0.7) + (bookingPct * 0.2) + (customsPct * 0.1);

    let state: 'complete' | 'partial' | 'blocked' = 'blocked';
    let label = 'Not Ready';
    let symbol = '⛔';

    if (readinessScore >= 90) {
      state = 'complete';
      label = 'Ready for Dispatch';
      symbol = '✔';
    } else if (readinessScore >= 70) {
      state = 'partial';
      label = 'Partial';
      symbol = '⚠';
    }

    const minDocsOk = shippingPrep.minDocs.every((name) => {
      const doc = shippingPrep.documents.find((d) => d.name === name);
      return doc && doc.status === 'verified';
    });

    return { readinessState: state, readinessLabel: label, readinessSymbol: symbol, minDocsMet: minDocsOk, readinessScore, docPct, bookingPct, customsPct };
  }, [shippingPrep, bookingCreated, selectedQuote]);

  const canUploadDocs = currentUserRole === 'Ops' || currentUserRole === 'Admin';
  const canQuote = !bookingCreated && (currentUserRole === 'Ops' || currentUserRole === 'Admin');
  const canBook = !bookingCreated && (currentUserRole === 'Ops' || currentUserRole === 'Admin');
  const customsDoc = shippingPrep?.documents.find((doc) => doc.name === 'Customs Declaration');
  const customsStatusLabel = customsDoc
    ? customsDoc.status === 'verified'
      ? 'Verified'
      : customsDoc.status === 'uploaded'
        ? 'Under review'
        : 'Pending upload'
    : 'Not started';

  const [previewFiles] = useState<File[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<Record<string, string>>({});

  const sortedQuotes = useMemo(() => {
    if (!shippingPrep?.quotes) return [] as CarrierQuote[];
    const q = [...shippingPrep.quotes];
    switch (quoteSort) {
      case 'eta':
        return q.sort((a, b) => a.etaDays - b.etaDays);
      case 'demurrage':
        return q.sort((a, b) => b.freeDemurrageDays - a.freeDemurrageDays);
      case 'price':
      default:
        return q.sort((a, b) => a.cost - b.cost);
    }
  }, [shippingPrep?.quotes, quoteSort]);

  const handleCreateBooking = async () => {
    if (!selectedQuote || !selectedVessel || !shippingPrep || !minDocsMet) return;

    let shipmentId = resolvedShipmentId;

    // No shipment record yet — create one on the fly before saving the booking
    if (!shipmentId && backendOrderId) {
      try {
        const result = await createOrderShipment({
          variables: {
            data: {
              orderId: backendOrderId,
              origin: originPort,
              destination: destinationPort,
            },
          },
        });
        shipmentId = (result as any)?.data?.createOrderShipments?.id;
        if (shipmentId) {
          setResolvedShipmentId(shipmentId);
          console.debug(`[Scheduling] Created shipment record id=${shipmentId} for orderId=${backendOrderId}`);
        }
      } catch (err) {
        console.error('[Scheduling] Failed to create shipment record:', err);
      }
    }

    if (!shipmentId) {
      console.error('[Scheduling] Cannot confirm booking: no shipment record and could not create one');
      return;
    }

    setBookingCreated(true);
    const newEntry: ActivityLogEntry = {
      id: `booking-${Date.now()}`,
      timestamp: new Date().toISOString(),
      type: 'system',
      actor: 'Scheduling System',
      action: 'Booking created',
      details: `Carrier: ${selectedQuote.carrier}, Vessel: ${selectedVessel.name}, Cost: $${selectedQuote.cost.toLocaleString()}, ETA: ${selectedVessel.eta}`,
    };
    setActivities((prev) => [newEntry, ...prev]);

    // Save booking to backend
    try {
      const shippingOptions = {
        carrier: selectedQuote.carrier,
        vessel: selectedVessel.name,
        vesselId: selectedVessel.id,
        voyage: selectedVessel.nextDeparture,
        eta: selectedVessel.eta,
        etd: selectedVessel.nextDeparture,
        cost: selectedQuote.cost,
        freeDemurrageDays: selectedQuote.freeDemurrageDays,
        containerCount: selectedVessel.containerCount,
        availableContainers: selectedVessel.availableContainers,
      };

      const shippingResponse = {
        ...shippingOptions,
        bookingReference: `${selectedQuote.id}-${Date.now()}`,
        bookingTime: new Date().toISOString(),
        status: 'confirmed',
      };

      console.debug('[Scheduling] Saving booking to backend:', {
        shipmentId,
        shippingProvider: selectedQuote.carrier,
        shippingOptions,
        shippingResponse,
      });

      await updateOrderShipment({
        variables: {
          input: {
            orderShipmentId: shipmentId,
            shippingProvider: selectedQuote.carrier,
            shippingOptions: JSON.stringify(shippingOptions),
            shippingResponse: JSON.stringify(shippingResponse),
          },
        },
      });

      console.debug('[Scheduling] Booking saved successfully');
    } catch (err) {
      console.error('[Scheduling] Failed to save booking to backend:', err);
    }

    // Regenerate SB with confirmed vessel/voyage details
    sb.updateWithVessel({
      vesselName: selectedVessel.name,
      voyageNumber: selectedVessel.nextDeparture || 'TBD',
      etd: selectedVessel.nextDeparture,
    }).catch(() => {});
  };

  return (
    <SchedulingContextContainer>
      <SchedulingContextHeader
        orderId={orderId}
        customerName={customerName}
        plant={plant}
        currentStage={currentStage}
        stageIndex={stageIndex}
        totalStages={totalStages}
        healthStatus={health}
        promisedDate={promisedDate}
        lastUpdate={lastUpdate}
        nextSystemAction={nextSystemAction}
      />

      <SchedulingTimeline
        stages={stages as any}
        currentStageIndex={stageIndex - 1}
      />


      {stageDetails && Object.keys(stageDetails).length > 0 && (
        <SchedulingDetailPanel
          currentStage={currentStage}
          stageDetails={stageDetails}
          isLoading={isLoadingStageDetails}
        />
      )}

      {shouldShowShippingPrep && shippingPrep && (
        <ShippingSection>
          <SectionHeader>
            <SectionHeading icon="Ship">Shipping Preparation & Booking</SectionHeading>
            <ReadinessBadgeGroup>
              <StatusBadge
                variant={readinessState === 'complete' ? 'success' : readinessState === 'partial' ? 'warning' : 'error'}
                label={`Shipping Readiness: ${readinessLabel}`}
                dot
              />
              <ReadinessStats>
                {Math.round(readinessScore)}% · Docs {Math.round(docPct)}% · Booking {Math.round(bookingPct)}% · Customs {Math.round(customsPct)}%
              </ReadinessStats>
            </ReadinessBadgeGroup>
          </SectionHeader>

          <ReadinessGrid>
            {shippingPrep.documents
              .filter((doc) => doc.name !== 'Customs Declaration')
              .map((doc) => {
              const badge = doc.requiredAfterBooking ? 'Generated' : doc.required ? 'Mandatory' : 'Optional';
              const isGenerated = doc.requiredAfterBooking;
              return (
                <DocCard key={doc.name} $status={doc.status}>
                  <DocMeta>
                    <DocName>{doc.name}</DocName>
                    <DocStatus $status={doc.status}>
                      <Icon name={doc.status === 'verified' ? 'CheckCircle' : 'AlertTriangle'} size={12} />
                      {doc.status === 'verified' ? 'Verified' : doc.status === 'uploaded' ? 'Pending Review' : 'Pending'}
                    </DocStatus>
                    <HelperText>{badge}</HelperText>
                    {isGenerated && (
                      <HelperText>Generated after booking</HelperText>
                    )}
                    {uploadErrors[doc.name] && (
                      <HelperText style={{ color: theme.colors.error, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Icon name="AlertTriangle" size={12} /> {uploadErrors[doc.name]}
                      </HelperText>
                    )}
                  </DocMeta>
                  <DocActions>
                    {isGenerated ? (
                      <Button size="sm" variant="ghost" disabled>
                        View Draft
                      </Button>
                    ) : (
                      <UploadField
                        label={undefined}
                        accept={undefined}
                        disabled={!canUploadDocs || doc.status === 'verified'}
                        hasFile={doc.status !== 'pending'}
                        onUpload={async (file) => {
                          setUploadErrors((prev) => ({ ...prev, [doc.name]: '' }));
                          try {
                            if (onUploadShippingDocument) {
                              await onUploadShippingDocument(doc.name, file);
                              return;
                            }
                            onUserAction?.(`upload-doc:${doc.name}`);
                          } catch (err) {
                            setUploadErrors((prev) => ({ ...prev, [doc.name]: (err as Error).message || 'Upload failed' }));
                          }
                        }}
                        onReplace={async (file) => {
                          setUploadErrors((prev) => ({ ...prev, [doc.name]: '' }));
                          try {
                            if (onReplaceShippingDocument) {
                              await onReplaceShippingDocument(doc.name, file);
                              return;
                            }
                            onUserAction?.(`replace-doc:${doc.name}`);
                          } catch (err) {
                            setUploadErrors((prev) => ({ ...prev, [doc.name]: (err as Error).message || 'Replace failed' }));
                          }
                        }}
                        onView={() => {
                          setUploadErrors((prev) => ({ ...prev, [doc.name]: '' }));
                          const resolvedUrl = onResolveDocumentUrl?.(doc.file);
                          if (resolvedUrl) {
                            window.open(resolvedUrl, '_blank', 'noopener,noreferrer');
                            return;
                          }
                          if (doc.file) {
                            window.open(doc.file, '_blank', 'noopener,noreferrer');
                            return;
                          }
                          setUploadErrors((prev) => ({
                            ...prev,
                            [doc.name]: 'No uploaded file available to preview',
                          }));
                        }}
                      />
                    )}
                  </DocActions>
                </DocCard>
              );
            })}
          </ReadinessGrid>

          {/* Compact compliance card to keep customs visible but lightweight */}
          <CompactCard>
            <div>
              <CompactTitle>Compliance (Blocking)</CompactTitle>
              <CompactMeta>
                <span>Status: {customsStatusLabel}</span>
                <span>Required to enable dispatch</span>
              </CompactMeta>
            </div>
            <CompactCTA size="sm" variant="outline" onClick={() => onUserAction?.('nav:customs')}>
              Manage →
            </CompactCTA>
          </CompactCard>

          {minDocsMet && (
            <SectionHeader style={{ marginTop: '8px' }}>
              <SectionHeading size="sm">Step 1: Carrier Selection</SectionHeading>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <HelperText>Choose a carrier based on cost, transit time, and terms</HelperText>
                <HelperText>Quotes are sourced from the connected logistics provider</HelperText>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: theme.colors.gray600 }}>
                  Sort by:
                  <select
                    value={quoteSort}
                    onChange={(e) => setQuoteSort(e.target.value as any)}
                    style={{
                      fontSize: 12,
                      padding: '6px 8px',
                      borderRadius: 6,
                      border: `1px solid ${theme.colors.borderStrong}`,
                      color: theme.colors.gray900,
                      background: theme.colors.surface,
                    }}
                  >
                    <option value="price">Price</option>
                    <option value="eta">ETA</option>
                    <option value="demurrage">Free demurrage</option>
                  </select>
                </label>
              </div>
            </SectionHeader>
          )}

          <QuotesSection $disabled={!minDocsMet}>
            {minDocsMet && shippingPrep.quotes && shippingPrep.quotes.length > 0 && (
              <CarrierQuotations
                quotes={sortedQuotes}
                selectedId={selectedQuote?.id}
                canSelect={canQuote}
                promisedDate={promisedDate}
                onSelect={(q) => {
                  setSelectedQuote(q);
                  setSelectedVessel(null);
                  if (q) setBookingCreated(false);
                }}
                onViewBreakup={(q) => {
                  setBreakupQuote(q);
                  onUserAction?.(`view-quote-breakup:${q.id}`);
                }}
              />
            )}

            {!minDocsMet && (
              <HelperText style={{ textAlign: 'center', fontStyle: 'italic', color: theme.colors.warningDark, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                <Icon name="AlertTriangle" size={12} /> All mandatory documents must be approved by Ops before carrier booking is available
              </HelperText>
            )}
          </QuotesSection>

          {/* STEP 2: Vessel Selection — remains visible after booking (read-only) */}
          {selectedQuote && (
            <>
              <ShippingWorkflowSteps>
                <StepIndicator $active={!bookingCreated} $completed={!!selectedQuote}>1</StepIndicator>
                <StepLabel>Carrier Selected</StepLabel>
                <Divider />
                <StepIndicator $active={!selectedVessel && !bookingCreated} $completed={!!selectedVessel}>2</StepIndicator>
                <StepLabel>{bookingCreated ? 'Vessel Booked' : 'Select Vessel'}</StepLabel>
                <Divider />
                <StepIndicator $active={false} $completed={bookingCreated}>
                  {bookingCreated ? <Icon name="CheckCircle" size={14} /> : '3'}
                </StepIndicator>
                <StepLabel>Confirm Booking</StepLabel>
              </ShippingWorkflowSteps>

              {(() => {
                const carrierKey = selectedQuote.carrier
                  .toLowerCase()
                  .replace(/&/g, '')
                  .replace(/\s+/g, '-')
                  .replace(/-$/, '');
                // Live API vessels take priority; fall back to prop-supplied vessels
                const vessels =
                  liveVesselsByCarrier[carrierKey] ||
                  liveVesselsByCarrier[selectedQuote.carrier.toLowerCase()] ||
                  vesselsByCarrier?.[carrierKey] ||
                  vesselsByCarrier?.[selectedQuote.carrier.toLowerCase()] ||
                  [];

                if (loadingVessels) {
                  return (
                    <div style={{ padding: '20px', textAlign: 'center', color: theme.colors.gray500, fontSize: '14px' }}>
                      Loading available vessels from {selectedQuote.carrier}…
                    </div>
                  );
                }

                return (
                  <AvailableVessels
                    carrierName={selectedQuote.carrier}
                    vessels={vessels}
                    selectedVesselId={selectedVessel?.id}
                    canSelect={canBook}
                    onSelect={setSelectedVessel}
                  />
                );
              })()}
            </>
          )}

          {/* STEP 3: Booking Confirmation */}
          {selectedQuote && selectedVessel && !bookingCreated && minDocsMet && (
            <>
              <ShippingWorkflowSteps>
                <StepIndicator $active={false} $completed={true}><Icon name="CheckCircle" size={14} /></StepIndicator>
                <StepLabel>Carrier Selected</StepLabel>
                <Divider />
                <StepIndicator $active={false} $completed={true}><Icon name="CheckCircle" size={14} /></StepIndicator>
                <StepLabel>Vessel Selected</StepLabel>
                <Divider />
                <StepIndicator $active={true} $completed={false}>3</StepIndicator>
                <StepLabel>Confirm Booking</StepLabel>
              </ShippingWorkflowSteps>

              <AfterSelectionCard>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <div style={{ fontWeight: 600, color: theme.colors.gray900, fontSize: 16 }}>Ready to Confirm Booking</div>
                    <div style={{ fontSize: '14px', color: theme.colors.gray600, marginTop: '8px' }}>
                      Carrier: <strong>{selectedQuote.carrier}</strong> · Vessel: <strong>{selectedVessel.name}</strong>
                    </div>
                    <div style={{ fontSize: '14px', color: theme.colors.gray600, marginTop: '4px' }}>
                      Departure: <strong>{selectedVessel.nextDeparture}</strong> · Arrival: <strong>{selectedVessel.eta}</strong> · Cost: <strong>${selectedQuote.cost.toLocaleString()}</strong>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <Button 
                      variant="ghost" 
                      onClick={() => setSelectedVessel(null)}
                    >
                      Change Vessel
                    </Button>
                    <Button 
                      disabled={!canBook} 
                      onClick={handleCreateBooking}
                    >
                      Confirm Booking
                    </Button>
                  </div>
                </div>
              </AfterSelectionCard>
            </>
          )}
        </ShippingSection>
      )}

      {bookingCreated && selectedQuote && selectedVessel && (
        <ShippingSummaryCard>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <SectionHeading icon="CheckCircle" size="sm">Shipping Booking Confirmed</SectionHeading>
            <Button size="sm" variant="ghost" onClick={() => onUserAction?.('view:booking-activity')}>
              View booking activity →
            </Button>
          </div>
          <SummaryRow>
            <SummaryRowLabel>Carrier</SummaryRowLabel>
            <SummaryRowValue>{selectedQuote.carrier}</SummaryRowValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryRowLabel>Vessel</SummaryRowLabel>
            <SummaryRowValue>{selectedVessel.name}</SummaryRowValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryRowLabel>Next Departure</SummaryRowLabel>
            <SummaryRowValue>{selectedVessel.nextDeparture}</SummaryRowValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryRowLabel>Est. Arrival</SummaryRowLabel>
            <SummaryRowValue>{selectedVessel.eta}</SummaryRowValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryRowLabel>Container Allocation</SummaryRowLabel>
            <SummaryRowValue>{selectedVessel.availableContainers} containers reserved</SummaryRowValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryRowLabel>Total Cost</SummaryRowLabel>
            <SummaryRowValue>${selectedQuote.cost.toLocaleString()}</SummaryRowValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryRowLabel>Free Demurrage</SummaryRowLabel>
            <SummaryRowValue>{selectedQuote.freeDemurrageDays} days</SummaryRowValue>
          </SummaryRow>
          {(() => {
            const eta = new Date(selectedVessel.eta);
            const promised = new Date(promisedDate);
            const daysLate = Math.ceil((eta.getTime() - promised.getTime()) / (1000 * 60 * 60 * 24));
            return (
              <SummaryRow>
                <SummaryRowLabel>Delay vs Promise</SummaryRowLabel>
                {daysLate > 0 ? (
                  <RiskBadge>
                    <Icon name="AlertTriangle" size={13} /> +{daysLate} days
                  </RiskBadge>
                ) : (
                  <SummaryRowValue style={{ color: theme.colors.successDark, display: 'flex', alignItems: 'center', gap: 4 }}>
                    On-time delivery <Icon name="CheckCircle" size={13} />
                  </SummaryRowValue>
                )}
              </SummaryRow>
            );
          })()}
          <SummaryRow>
            <SummaryRowLabel>Booked By</SummaryRowLabel>
            <SummaryRowValue>{currentUser || 'Operations'}</SummaryRowValue>
          </SummaryRow>
        </ShippingSummaryCard>
      )}

      {shouldShowShippingPrep && (
        <ShippingBillCard
          state={sb.state}
          currentStage={currentStage}
          isLoading={sb.isLoading}
          error={sb.error}
          onGenerateDraft={sb.generateDraft}
          onMarkFiled={sb.markFiled}
          onMarkLeoGranted={sb.markLeoGranted}
        />
      )}

      {vessels && vessels.length > 0 && (
        <VesselSchedule
          currentStage={currentStage}
          stageIndex={stageIndex}
          vessels={vessels}
          promisedDate={promisedDate}
          isLoading={isLoadingVessels}
        />
      )}

      {/* Downplay generic actions when shipping workflow is present */}
      {!shippingPrep && onUserAction && (
        <SchedulingActions
          currentStage={currentStage}
          onAction={handleUserAction}
        />
      )}

      {showAddNotePanel && (
        <SchedulingAddNote
          orderId={orderId}
          currentStage={currentStage}
          currentHealth={health}
          timestamp={new Date().toISOString()}
          currentUser={currentUser}
          onSave={handleAddNoteSubmit}
          onCancel={() => setShowAddNotePanel(false)}
        />
      )}

      <SchedulingNotes
        notes={savedNotes}
        isLoading={isLoadingNotes}
        actions={<Button size="default" onClick={() => setShowAddNotePanel(true)}>Add Note</Button>}
      />

      {/* Read-only next actions to keep the main page clean and navigable */}
      <NextStepsPanel>
        <NextStepsHeader>
          <Icon name="Info" size={14} /> Next Required Actions
        </NextStepsHeader>
        <NextStepsTable>
          <NextStepsHeaderCell>Step</NextStepsHeaderCell>
          <NextStepsHeaderCell>Status</NextStepsHeaderCell>
          <NextStepsHeaderCell>Owner</NextStepsHeaderCell>
          <NextStepsHeaderCell>Action</NextStepsHeaderCell>

          <NextStepsCell>Shipping Bill (ICEGate)</NextStepsCell>
          <NextStepsCell>
            <StatusBadge
              variant={
                sb.state.status === 'leo_granted' ? 'ready'
                : sb.state.status === 'not_started' ? 'locked'
                : 'pending'
              }
              size="sm"
              label={
                sb.state.status === 'leo_granted' ? 'LEO Granted'
                  : sb.state.status === 'filed' ? 'Filed'
                  : sb.state.status === 'vessel_updated' ? 'Vessel Added'
                  : sb.state.status === 'drafted' ? 'Draft Ready'
                  : sb.state.status === 'drafting' ? 'Generating...'
                  : 'Not Started'
              }
            />
          </NextStepsCell>
          <NextStepsCell>Compliance</NextStepsCell>
          <NextStepsCell>Auto-generated at Production Ready</NextStepsCell>

          <NextStepsCell>Customs Declaration</NextStepsCell>
          <NextStepsCell><StatusBadge variant="pending" size="sm" label="Pending" /></NextStepsCell>
          <NextStepsCell>Compliance</NextStepsCell>
          <NextStepsCell>
            <Button size="sm" variant="ghost" onClick={() => onUserAction?.('nav:customs')}>Go to Customs →</Button>
          </NextStepsCell>

          <NextStepsCell>Bill of Lading</NextStepsCell>
          <NextStepsCell><StatusBadge variant="locked" size="sm" label="Locked" /></NextStepsCell>
          <NextStepsCell>System</NextStepsCell>
          <NextStepsCell>Auto after customs</NextStepsCell>

          <NextStepsCell>Carrier Docs Submission</NextStepsCell>
          <NextStepsCell><StatusBadge variant="pending" size="sm" label="Pending" /></NextStepsCell>
          <NextStepsCell>Ops</NextStepsCell>
          <NextStepsCell>
            <Button size="sm" variant="ghost" onClick={() => onUserAction?.('nav:carrier-docs')}>View checklist</Button>
          </NextStepsCell>

          <NextStepsCell>Dispatch Scheduling</NextStepsCell>
          <NextStepsCell><StatusBadge variant="locked" size="sm" label="Locked" /></NextStepsCell>
          <NextStepsCell>Ops</NextStepsCell>
          <NextStepsCell>After customs</NextStepsCell>

          <NextStepsCell>Carrier Manifest Submission</NextStepsCell>
          <NextStepsCell><StatusBadge variant="locked" size="sm" label="Locked" /></NextStepsCell>
          <NextStepsCell>System</NextStepsCell>
          <NextStepsCell>Auto after B/L</NextStepsCell>
        </NextStepsTable>
      </NextStepsPanel>

      {showPreview && (
        <ModalOverlay>
          <PreviewModalPanel onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Document Preview</ModalTitle>
              <Button size="sm" variant="ghost" onClick={() => setShowPreview(false)}>Close</Button>
            </ModalHeader>
            <PreviewModalBody>
              <FileListPreview files={previewFiles} />
            </PreviewModalBody>
          </PreviewModalPanel>
        </ModalOverlay>
      )}

      {breakupQuote && (
        <ModalOverlay $dim={0.5} onClick={() => setBreakupQuote(null)}>
          <BreakupModalPanel onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <SectionHeading icon="DollarSign" size="sm">Quote Breakup · {breakupQuote.carrier}</SectionHeading>
              <Button size="sm" variant="ghost" onClick={() => setBreakupQuote(null)}>
                <Icon name="X" size={14} />
              </Button>
            </ModalHeader>
            <BreakupBody>
              {/* Summary Section */}
              <BreakupSummaryCard>
                <BreakupSummaryLabel>Total Freight Cost</BreakupSummaryLabel>
                <BreakupSummaryValue>${breakupQuote.cost.toLocaleString()}</BreakupSummaryValue>
                <BreakupSummaryHint>All-inclusive shipping quote</BreakupSummaryHint>
              </BreakupSummaryCard>

              {/* Cost Breakdown */}
              <BreakupSection>
                <BreakupSectionTitle>Cost Breakdown</BreakupSectionTitle>
                <BreakupRows>
                  <BreakupRow>
                    <BreakupRowLabel>Ocean Freight</BreakupRowLabel>
                    <BreakupRowValue>${(breakupQuote.cost * 0.65).toLocaleString(undefined, { maximumFractionDigits: 0 })}</BreakupRowValue>
                  </BreakupRow>
                  <BreakupRow>
                    <BreakupRowLabel>Terminal Handling (THC)</BreakupRowLabel>
                    <BreakupRowValue>${(breakupQuote.cost * 0.15).toLocaleString(undefined, { maximumFractionDigits: 0 })}</BreakupRowValue>
                  </BreakupRow>
                  <BreakupRow>
                    <BreakupRowLabel>Documentation Fees</BreakupRowLabel>
                    <BreakupRowValue>${(breakupQuote.cost * 0.08).toLocaleString(undefined, { maximumFractionDigits: 0 })}</BreakupRowValue>
                  </BreakupRow>
                  <BreakupRow>
                    <BreakupRowLabel>Fuel Surcharge (BAF)</BreakupRowLabel>
                    <BreakupRowValue>${(breakupQuote.cost * 0.10).toLocaleString(undefined, { maximumFractionDigits: 0 })}</BreakupRowValue>
                  </BreakupRow>
                  <BreakupTotalRow>
                    <BreakupTotalLabel>Total</BreakupTotalLabel>
                    <BreakupTotalLabel>${breakupQuote.cost.toLocaleString()}</BreakupTotalLabel>
                  </BreakupTotalRow>
                </BreakupRows>
              </BreakupSection>

              {/* Service Details */}
              <BreakupSection>
                <BreakupSectionTitle>Service Details</BreakupSectionTitle>
                <BreakupDetailGrid>
                  <div>
                    <BreakupDetailLabel>Free Demurrage</BreakupDetailLabel>
                    <BreakupDetailValue>{breakupQuote.freeDemurrageDays} days</BreakupDetailValue>
                  </div>
                  <div>
                    <BreakupDetailLabel>Transit Time</BreakupDetailLabel>
                    <BreakupDetailValue>{breakupQuote.transit}</BreakupDetailValue>
                  </div>
                  <div>
                    <BreakupDetailLabel>ETA Days</BreakupDetailLabel>
                    <BreakupDetailValue>{breakupQuote.etaDays} days</BreakupDetailValue>
                  </div>
                  <div>
                    <BreakupDetailLabel>Service Type</BreakupDetailLabel>
                    <BreakupDetailValue>{breakupQuote.transit.includes('Direct') ? 'Direct' : 'Transshipment'}</BreakupDetailValue>
                  </div>
                </BreakupDetailGrid>
              </BreakupSection>

              {/* Schedule */}
              <div>
                <BreakupSectionTitle>Estimated Schedule</BreakupSectionTitle>
                <BreakupDetailGrid>
                  <div>
                    <BreakupDetailLabel>Est. Departure</BreakupDetailLabel>
                    <BreakupDetailValue>{breakupQuote.estimatedDeparture || 'TBD'}</BreakupDetailValue>
                  </div>
                  <div>
                    <BreakupDetailLabel>Est. Arrival</BreakupDetailLabel>
                    <BreakupDetailValue>{breakupQuote.estimatedArrival || 'TBD'}</BreakupDetailValue>
                  </div>
                </BreakupDetailGrid>
              </div>

              {/* Additional Info */}
              <BreakupNote>
                <BreakupNoteLabel>
                  <Icon name="Info" size={12} /> Note:
                </BreakupNoteLabel>
                <BreakupNoteText>
                  Rates are subject to carrier confirmation. Additional charges may apply for oversized cargo, hazardous materials, or special handling requirements.
                </BreakupNoteText>
              </BreakupNote>
            </BreakupBody>
            <ModalFooter>
              <Button size="sm" variant="outline" onClick={() => setBreakupQuote(null)}>Close</Button>
            </ModalFooter>
          </BreakupModalPanel>
        </ModalOverlay>
      )}

      <SchedulingActivityLog
        entries={activities}
        isLoading={isLoadingActivities}
      />
    </SchedulingContextContainer>
  );
};
